import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Save, Trash2, GripVertical, DollarSign, 
  Eye, Edit, Copy, Settings, Tag, FolderTree, Sparkles, X, Loader2, Upload, ImageIcon,
  Download, FileUp, FileDown, ChevronUp, ChevronDown, MoreVertical
} from 'lucide-react';

/**
 * StepGuideImageUpload - Upload guide image for visual guide layout
 * Uses fetch API with proper token refresh handling via apiCall
 */
const StepGuideImageUpload = ({ imageUrl, onImageChange, apiCall }) => {
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const fileInputRef = React.useRef(null);
  
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }
    
    setUploading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Use apiCall which handles token refresh properly
      // Skip server optimization for faster uploads
      const response = await apiCall('/api/media/upload?optimize=false', {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for FormData
        headers: {}
      });
      
      if (response && (response.file_url || response.do_source_url || response.url)) {
        const imageUrl = response.file_url || response.do_source_url || response.url;
        onImageChange(imageUrl);
      } else {
        setError('Upload failed: Invalid response');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  // Helper to get full image URL
  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    return `${BACKEND_URL}${url}`;
  };
  
  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
      
      {imageUrl ? (
        <div className="relative group">
          <img 
            src={getFullImageUrl(imageUrl)} 
            alt="Guide" 
            className="w-32 h-32 object-contain rounded-lg border-2 border-purple-300 bg-gray-50"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100"
              title="Change image"
            >
              <Upload className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onImageChange(null)}
              className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"
              title="Remove image"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-32 h-32 border-2 border-dashed border-purple-300 rounded-lg flex flex-col items-center justify-center text-purple-500 hover:bg-purple-50 hover:border-purple-400 transition-colors"
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : (
            <>
              <ImageIcon className="h-8 w-8 mb-1" />
              <span className="text-xs">Upload Image</span>
            </>
          )}
        </button>
      )}
      
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};
import { useToast } from '../hooks/use-toast';
import ProductOptionFieldRenderer from '../components/ProductOptionFieldRenderer';

// Generate unique ID
const generateId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ProductOptionTemplateBuilder = () => {
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [pricingEnabled, setPricingEnabled] = useState(false);
  
  // AI Generation states
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [previewValues, setPreviewValues] = useState({});
  const [draggedField, setDraggedField] = useState(null);
  
  // Import/Export states
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);

  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    tags: [],
    category_ids: [],
    subcategory_ids: [],
    steps: [{
      id: generateId(),
      name: 'Step 1',
      description: '',
      fields: []
    }],
    is_active: true
  });

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'number', label: 'Number Input' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'date', label: 'Date Picker' },
    { value: 'time', label: 'Time Picker' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'radio', label: 'Radio Buttons' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'button-select', label: 'Button Select' },
    { value: 'image-cards', label: 'Image Cards' },
    { value: 'gallery', label: 'Image Gallery' },
    { value: 'file_upload', label: 'File Upload' }
  ];

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
    fetchPricingConfig();
  }, []);

  useEffect(() => {
  }, [categories]);

  const fetchTemplates = async () => {
    try {
      const data = await apiCall('/api/product-option-templates');
      setTemplates(data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await apiCall('/api/categories/');
      if (data && data.length > 0) {
        setCategories(data);
      } else {
        throw new Error('No categories returned');
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      
      // Fallback: Fetch directly with full HTTPS URL to bypass mixed content
      try {
        const response = await fetch(`${window.location.origin}/api/categories/`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data || []);
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
      
      // Final fallback: Use cached/static data
      setCategories([]);
      toast({
        title: 'Categories Unavailable',
        description: 'Using mixed content security. Templates will work but category assignment may be limited.',
        variant: 'default'
      });
    }
  };

  const fetchPricingConfig = async () => {
    try {
      const data = await apiCall('/api/product-option-templates/system/pricing-config');
      setPricingEnabled(data.master_pricing_enabled);
    } catch (error) {
      console.error('Failed to fetch pricing config:', error);
    }
  };

  const togglePricingConfig = async (enabled) => {
    try {
      const data = await apiCall(`/api/product-option-templates/system/pricing-config?enabled=${enabled}`, {
        method: 'POST'
      });
      setPricingEnabled(data.enabled);
      toast({
        title: enabled ? 'Automatic Pricing Enabled' : 'Manual Pricing Mode',
        description: enabled 
          ? 'Product option prices will be calculated automatically'
          : 'Price differences will be collected via WhatsApp'
      });
    } catch (error) {
      console.error('Failed to update pricing config:', error);
      toast({
        title: 'Error',
        description: 'Failed to update pricing configuration',
        variant: 'destructive'
      });
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setTemplateData({
      name: '',
      description: '',
      tags: [],
      category_ids: [],
      subcategory_ids: [],
      steps: [{
        id: generateId(),
        name: 'Step 1',
        description: '',
        layout: 'standard',
        guide_image: null,
        image_position: 'left',
        bg_style: 'dark',
        fields: []
      }],
      is_active: true
    });
    setShowBuilder(true);
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a description for your template',
        variant: 'destructive'
      });
      return;
    }

    setAiLoading(true);
    try {
      const response = await apiCall('/api/ai-assistant/generate-workflow', {
        method: 'POST',
        body: JSON.stringify({
          prompt: aiPrompt,
          model: 'gpt5'
        })
      });

      // Convert AI workflow to template format
      const aiTemplate = {
        name: response.workflow_name || 'AI Generated Template',
        description: `Generated from: ${aiPrompt}`,
        tags: response.tags || [],
        category_ids: [],
        subcategory_ids: [],
        steps: response.steps.map((step, idx) => ({
          id: generateId(),
          name: step.title || `Step ${idx + 1}`,
          description: step.description || '',
          fields: step.fields.map((field, fidx) => ({
            id: generateId(),
            type: field.type === 'buttonselect' ? 'button-select' : 
                  field.type === 'imageselect' ? 'image-cards' :
                  field.type === 'select' ? 'dropdown' :
                  field.type,
            name: field.name,
            label: field.label,
            placeholder: field.placeholder || '',
            help_text: field.helpText || '',
            required: field.required || false,
            options: field.options?.map((opt, oidx) => ({
              label: typeof opt === 'string' ? opt : opt.label,
              value: `option_${generateId()}`,
              default_price: 0,
              image_url: field.imageOptions?.[oidx] || ''
            })) || [],
            price_impact: false,
            default_mechanism: 'variant'
          }))
        })),
        is_active: true
      };

      setTemplateData(aiTemplate);
      setShowAIDialog(false);
      setShowBuilder(true);
      toast({
        title: '✨ AI Template Generated',
        description: 'Template created! You can now customize fields and add pricing.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate template with AI',
        variant: 'destructive'
      });
    } finally {
      setAiLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    
    // Normalize template data - add missing layout fields for backward compatibility
    const normalizedTemplate = {
      ...template,
      steps: (template.steps || []).map(step => ({
        ...step,
        layout: step.layout || 'standard',
        guide_image: step.guide_image || null,
        image_position: step.image_position || 'left',
        bg_style: step.bg_style || 'dark',
        fields: (step.fields || []).map(field => ({
          ...field,
          // Normalize field properties
          allowed_types: field.allowed_types || ['image/*', '.pdf', '.doc', '.docx'],
          max_size: field.max_size || 10
        }))
      }))
    };
    
    setTemplateData(normalizedTemplate);
    setShowBuilder(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateData.name) {
      toast({
        title: 'Error',
        description: 'Template name is required',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      if (editingTemplate) {
        await apiCall(`/api/product-option-templates/${editingTemplate.id}`, {
          method: 'PUT',
          body: JSON.stringify(templateData)
        });
        toast({ title: 'Success', description: 'Template updated successfully' });
      } else {
        await apiCall('/api/product-option-templates', {
          method: 'POST',
          body: JSON.stringify(templateData)
        });
        toast({ title: 'Success', description: 'Template created successfully' });
      }
      setShowBuilder(false);
      fetchTemplates();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save template',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await apiCall(`/api/product-option-templates/${templateId}`, {
        method: 'DELETE'
      });
      toast({ title: 'Success', description: 'Template deleted successfully' });
      fetchTemplates();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete template',
        variant: 'destructive'
      });
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await apiCall(`/api/product-option-templates/${template.id}/duplicate`, { method: 'POST' });
      toast({ title: 'Template duplicated', description: `"${template.name} (Copy)" created as inactive.` });
      fetchTemplates();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to duplicate template',
        variant: 'destructive'
      });
    }
  };

  // ==================== IMPORT/EXPORT ====================
  
  const handleExportJSON = async () => {
    try {
      const data = await apiCall('/api/product-option-templates/export/json');
      
      // Create downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `product_option_templates_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: 'Export Successful',
        description: `Exported ${data.total_templates} templates`
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export templates',
        variant: 'destructive'
      });
    }
  };
  
  const handleImportJSON = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate JSON structure
      let templates = [];
      if (Array.isArray(data)) {
        templates = data;
      } else if (data.templates && Array.isArray(data.templates)) {
        templates = data.templates;
      } else {
        throw new Error('Invalid JSON format. Expected an array of templates or an object with "templates" array.');
      }
      
      if (templates.length === 0) {
        throw new Error('No templates found in the file');
      }
      
      // Send to backend
      const result = await apiCall('/api/product-option-templates/import/json', {
        method: 'POST',
        body: JSON.stringify({
          templates: templates,
          merge_strategy: 'upsert'
        })
      });
      
      toast({
        title: 'Import Successful',
        description: result.message
      });
      
      // Refresh templates list
      fetchTemplates();
      
    } catch (error) {
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to import templates',
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const addStep = () => {
    setTemplateData(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          id: generateId(),
          name: `Step ${prev.steps.length + 1}`,
          description: '',
          layout: 'standard',
          guide_image: null,
          image_position: 'left',
          bg_style: 'dark',
          fields: []
        }
      ]
    }));
  };

  // Remove a step/section entirely
  const removeStep = (stepIndex) => {
    if (templateData.steps.length <= 1) {
      toast({
        title: 'Cannot Delete',
        description: 'Template must have at least one section',
        variant: 'destructive'
      });
      return;
    }
    setTemplateData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, idx) => idx !== stepIndex)
    }));
  };

  // Move step up or down for reordering
  const moveStep = (stepIndex, direction) => {
    const newIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
    if (newIndex < 0 || newIndex >= templateData.steps.length) return;
    
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      const [movedStep] = newSteps.splice(stepIndex, 1);
      newSteps.splice(newIndex, 0, movedStep);
      return { ...prev, steps: newSteps };
    });
  };

  // Duplicate a step/section
  const duplicateStep = (stepIndex) => {
    const stepToDuplicate = templateData.steps[stepIndex];
    const duplicatedStep = {
      ...JSON.parse(JSON.stringify(stepToDuplicate)), // Deep clone
      id: generateId(),
      name: `${stepToDuplicate.name} (Copy)`,
      fields: stepToDuplicate.fields.map(field => ({
        ...field,
        id: generateId(),
        name: `field_${generateId()}`
      }))
    };
    
    setTemplateData(prev => ({
      ...prev,
      steps: [
        ...prev.steps.slice(0, stepIndex + 1),
        duplicatedStep,
        ...prev.steps.slice(stepIndex + 1)
      ]
    }));
  };

  const addField = (stepIndex) => {
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      newSteps[stepIndex].fields.push({
        id: generateId(),
        type: 'text',
        name: `field_${generateId()}`,
        label: '',
        placeholder: '',
        help_text: '',
        required: false,
        options: [],
        price_impact: false,
        default_mechanism: 'variant',
        default_multiplier_type: 'fixed'  // NEW: Default to fixed surcharge
      });
      return { ...prev, steps: newSteps };
    });
  };

  const updateField = (stepIndex, fieldIndex, updates) => {
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      newSteps[stepIndex].fields[fieldIndex] = {
        ...newSteps[stepIndex].fields[fieldIndex],
        ...updates
      };
      return { ...prev, steps: newSteps };
    });
  };

  const removeField = (stepIndex, fieldIndex) => {
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      newSteps[stepIndex].fields.splice(fieldIndex, 1);
      return { ...prev, steps: newSteps };
    });
  };

  const addOption = (stepIndex, fieldIndex) => {
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      const field = newSteps[stepIndex].fields[fieldIndex];
      if (!field.options) field.options = [];
      const optionId = `option_${generateId()}`;
      field.options.push({
        label: '',
        value: optionId,
        default_price: 0,
        image_url: '' // For image-cards and gallery types
      });
      return { ...prev, steps: newSteps };
    });
  };

  const handleImageUpload = async (stepIndex, fieldIndex, optionIndex, file) => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiCall('/api/files/upload', {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set Content-Type with boundary
      });

      if (response.url) {
        updateOption(stepIndex, fieldIndex, optionIndex, { image_url: response.url });
        toast({ title: 'Success', description: 'Image uploaded successfully' });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive'
      });
    }
  };

  const updateOption = (stepIndex, fieldIndex, optionIndex, updates) => {
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      newSteps[stepIndex].fields[fieldIndex].options[optionIndex] = {
        ...newSteps[stepIndex].fields[fieldIndex].options[optionIndex],
        ...updates
      };
      return { ...prev, steps: newSteps };
    });
  };

  const removeOption = (stepIndex, fieldIndex, optionIndex) => {
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      newSteps[stepIndex].fields[fieldIndex].options.splice(optionIndex, 1);
      return { ...prev, steps: newSteps };
    });
  };

  const handleDragStart = (e, stepIndex, fieldIndex) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ stepIndex, fieldIndex }));
    setDraggedField({ stepIndex, fieldIndex });
    // Add visual feedback
    e.target.classList.add('opacity-50');
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('opacity-50');
    setDraggedField(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, stepIndex, fieldIndex) => {
    e.preventDefault();
    if (draggedField && draggedField.stepIndex === stepIndex && draggedField.fieldIndex !== fieldIndex) {
      e.currentTarget.classList.add('border-blue-500', 'border-2');
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('border-blue-500', 'border-2');
  };

  const handleDrop = (e, stepIndex, fieldIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-blue-500', 'border-2');
    
    if (!draggedField) return;
    
    if (draggedField.stepIndex !== stepIndex) {
      toast({
        title: 'Not Allowed',
        description: 'Cannot move fields between steps',
        variant: 'destructive'
      });
      setDraggedField(null);
      return;
    }

    if (draggedField.fieldIndex === fieldIndex) {
      setDraggedField(null);
      return;
    }

    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      const fields = [...newSteps[stepIndex].fields];
      const [movedField] = fields.splice(draggedField.fieldIndex, 1);
      fields.splice(fieldIndex, 0, movedField);
      newSteps[stepIndex].fields = fields;
      return { ...prev, steps: newSteps };
    });
    
    toast({
      title: 'Field Reordered',
      description: 'Field position updated successfully',
    });
    
    setDraggedField(null);
  };

  // Move field up/down buttons as alternative to drag
  const moveField = (stepIndex, fieldIndex, direction) => {
    const newIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    
    setTemplateData(prev => {
      const newSteps = [...prev.steps];
      const fields = [...newSteps[stepIndex].fields];
      
      if (newIndex < 0 || newIndex >= fields.length) return prev;
      
      const [movedField] = fields.splice(fieldIndex, 1);
      fields.splice(newIndex, 0, movedField);
      newSteps[stepIndex].fields = fields;
      return { ...prev, steps: newSteps };
    });
  };

  if (showBuilder) {
    return (
      <DashboardLayout>
        <div className="flex gap-6 h-[calc(100vh-8rem)]">
          {/* Left: Builder Panel */}
          <div className="flex-1 space-y-6 overflow-y-auto pr-4">
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold">
                    {editingTemplate ? 'Edit Template' : 'Create Template'}
                  </h1>
                  <p className="text-gray-600 text-sm mt-1">
                    Build dynamic product option forms
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowBuilder(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveTemplate} disabled={loading}>
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>

          {/* Template Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Template Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={templateData.name}
                  onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })}
                  placeholder="e.g., T-Shirt Customization"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={templateData.description}
                  onChange={(e) => setTemplateData({ ...templateData, description: e.target.value })}
                  placeholder="Describe what this template is for..."
                  rows={3}
                />
              </div>
              
              {/* Category Assignment - Multi-Select */}
              <div className="space-y-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <FolderTree className="h-4 w-4" />
                    Assign to Categories & Subcategories
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Select multiple categories where this template will be available
                  </p>
                  
                  {/* Selected Categories Display */}
                  {(templateData.category_ids?.length > 0 || templateData.subcategory_ids?.length > 0) && (
                    <div className="flex flex-wrap gap-2 mb-3 p-2 bg-blue-50 rounded-lg">
                      {templateData.category_ids?.map(catId => {
                        const cat = categories.find(c => c.id === catId);
                        return cat ? (
                          <Badge key={catId} variant="secondary" className="flex items-center gap-1">
                            {cat.name}
                            <X 
                              className="h-3 w-3 cursor-pointer hover:text-red-500" 
                              onClick={() => {
                                // Remove category and its subcategories
                                const subcatIds = cat.subcategories?.map(s => s.id) || [];
                                setTemplateData({
                                  ...templateData,
                                  category_ids: templateData.category_ids.filter(id => id !== catId),
                                  subcategory_ids: templateData.subcategory_ids.filter(id => !subcatIds.includes(id))
                                });
                              }}
                            />
                          </Badge>
                        ) : null;
                      })}
                      {templateData.subcategory_ids?.map(subId => {
                        // Find the subcategory
                        for (const cat of categories) {
                          const sub = cat.subcategories?.find(s => s.id === subId);
                          if (sub) {
                            return (
                              <Badge key={subId} variant="outline" className="flex items-center gap-1 text-xs">
                                {cat.name} → {sub.name}
                                <X 
                                  className="h-3 w-3 cursor-pointer hover:text-red-500" 
                                  onClick={() => {
                                    setTemplateData({
                                      ...templateData,
                                      subcategory_ids: templateData.subcategory_ids.filter(id => id !== subId)
                                    });
                                  }}
                                />
                              </Badge>
                            );
                          }
                        }
                        return null;
                      })}
                    </div>
                  )}
                  
                  {/* Category Multi-Select Grid */}
                  <div className="max-h-64 overflow-y-auto border rounded-lg p-3 space-y-3">
                    {categories.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">Loading categories...</p>
                    ) : (
                      categories.map(cat => (
                        <div key={cat.id} className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={templateData.category_ids?.includes(cat.id) || false}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setTemplateData({
                                    ...templateData,
                                    category_ids: [...(templateData.category_ids || []), cat.id]
                                  });
                                } else {
                                  // Remove category and its subcategories
                                  const subcatIds = cat.subcategories?.map(s => s.id) || [];
                                  setTemplateData({
                                    ...templateData,
                                    category_ids: templateData.category_ids.filter(id => id !== cat.id),
                                    subcategory_ids: templateData.subcategory_ids.filter(id => !subcatIds.includes(id))
                                  });
                                }
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="font-medium text-sm">{cat.name}</span>
                            {cat.subcategories?.length > 0 && (
                              <span className="text-xs text-gray-400">({cat.subcategories.length} subcategories)</span>
                            )}
                          </label>
                          
                          {/* Subcategories - show when category is selected */}
                          {templateData.category_ids?.includes(cat.id) && cat.subcategories?.length > 0 && (
                            <div className="ml-6 pl-3 border-l-2 border-gray-200 space-y-1">
                              {cat.subcategories.map(sub => (
                                <label key={sub.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded text-sm">
                                  <input
                                    type="checkbox"
                                    checked={templateData.subcategory_ids?.includes(sub.id) || false}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setTemplateData({
                                          ...templateData,
                                          subcategory_ids: [...(templateData.subcategory_ids || []), sub.id]
                                        });
                                      } else {
                                        setTemplateData({
                                          ...templateData,
                                          subcategory_ids: templateData.subcategory_ids.filter(id => id !== sub.id)
                                        });
                                      }
                                    }}
                                    className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-gray-600">{sub.name}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {templateData.category_ids?.length || 0} categories and {templateData.subcategory_ids?.length || 0} subcategories selected
                  </p>
                </div>
              </div>
              
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={templateData.tags.join(', ')}
                  onChange={(e) => setTemplateData({
                    ...templateData,
                    tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                  })}
                  placeholder="e.g., FOOD, SERVICE, APPAREL"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add tags to help organize templates
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Steps and Fields Builder */}
          {templateData.steps.map((step, stepIndex) => (
            <Card key={step.id} className="relative">
              <CardHeader>
                <div className="flex items-center justify-between">
                  {/* Section Reorder Controls */}
                  <div className="flex items-center gap-1 mr-3">
                    <button
                      type="button"
                      onClick={() => moveStep(stepIndex, 'up')}
                      disabled={stepIndex === 0}
                      className={`p-1 rounded hover:bg-gray-100 transition-colors ${stepIndex === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`}
                      title="Move section up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(stepIndex, 'down')}
                      disabled={stepIndex === templateData.steps.length - 1}
                      className={`p-1 rounded hover:bg-gray-100 transition-colors ${stepIndex === templateData.steps.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`}
                      title="Move section down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        Section {stepIndex + 1}
                      </Badge>
                      <Input
                        value={step.name}
                        onChange={(e) => {
                          const newSteps = [...templateData.steps];
                          newSteps[stepIndex].name = e.target.value;
                          setTemplateData({ ...templateData, steps: newSteps });
                        }}
                        className="text-lg font-semibold border-none p-0 focus:ring-0"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Layout Type Selector */}
                    <Select
                      value={step.layout || 'standard'}
                      onValueChange={(value) => {
                        const newSteps = [...templateData.steps];
                        newSteps[stepIndex].layout = value;
                        setTemplateData({ ...templateData, steps: newSteps });
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Layout" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard Layout</SelectItem>
                        <SelectItem value="visual_guide">Visual Guide (Image + Fields)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addField(stepIndex)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Field
                    </Button>
                    
                    {/* Section Actions Dropdown */}
                    <div className="relative group">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                      <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                        <button
                          type="button"
                          onClick={() => duplicateStep(stepIndex)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Copy className="h-4 w-4" />
                          Duplicate Section
                        </button>
                        <button
                          type="button"
                          onClick={() => removeStep(stepIndex)}
                          disabled={templateData.steps.length <= 1}
                          className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${templateData.steps.length <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete Section
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Visual Guide Layout Configuration */}
                {step.layout === 'visual_guide' && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <Label className="text-sm font-medium text-purple-700 mb-2 block">Guide Image</Label>
                        <StepGuideImageUpload
                          imageUrl={step.guide_image}
                          onImageChange={(url) => {
                            const newSteps = [...templateData.steps];
                            newSteps[stepIndex].guide_image = url;
                            setTemplateData({ ...templateData, steps: newSteps });
                          }}
                          apiCall={apiCall}
                        />
                      </div>
                      <div className="flex-1 space-y-3">
                        <div>
                          <Label className="text-sm">Step Description (shown below image)</Label>
                          <Textarea
                            value={step.description || ''}
                            onChange={(e) => {
                              const newSteps = [...templateData.steps];
                              newSteps[stepIndex].description = e.target.value;
                              setTemplateData({ ...templateData, steps: newSteps });
                            }}
                            placeholder="e.g., Use the reference image to identify measurement points"
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Image Position</Label>
                            <Select
                              value={step.image_position || 'left'}
                              onValueChange={(value) => {
                                const newSteps = [...templateData.steps];
                                newSteps[stepIndex].image_position = value;
                                setTemplateData({ ...templateData, steps: newSteps });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="left">Image on Left</SelectItem>
                                <SelectItem value="right">Image on Right</SelectItem>
                                <SelectItem value="top">Image on Top</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Background Style</Label>
                            <Select
                              value={step.bg_style || 'dark'}
                              onValueChange={(value) => {
                                const newSteps = [...templateData.steps];
                                newSteps[stepIndex].bg_style = value;
                                setTemplateData({ ...templateData, steps: newSteps });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="dark">Dark Background</SelectItem>
                                <SelectItem value="light">Light Background</SelectItem>
                                <SelectItem value="gradient">Gradient</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {step.fields.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                    <p>No fields yet. Click &quot;Add Field&quot; to start building.</p>
                  </div>
                ) : (
                  step.fields.map((field, fieldIndex) => (
                  <Card 
                    key={field.id} 
                    className={`border-2 transition-colors ${
                      draggedField?.stepIndex === stepIndex && draggedField?.fieldIndex === fieldIndex 
                        ? 'opacity-50 border-gray-300' 
                        : 'hover:border-blue-300'
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, stepIndex, fieldIndex)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, stepIndex, fieldIndex)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, stepIndex, fieldIndex)}
                  >
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex items-start gap-4">
                        {/* Drag Handle and Reorder Buttons */}
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <GripVertical className="h-5 w-5 text-gray-400 cursor-grab active:cursor-grabbing" />
                          <div className="flex flex-col gap-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              disabled={fieldIndex === 0}
                              onClick={() => moveField(stepIndex, fieldIndex, 'up')}
                              title="Move Up"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              disabled={fieldIndex === step.fields.length - 1}
                              onClick={() => moveField(stepIndex, fieldIndex, 'down')}
                              title="Move Down"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex-1 space-y-4">
                          {/* Field Type and Label */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Field Type</Label>
                              <Select
                                value={field.type}
                                onValueChange={(value) => updateField(stepIndex, fieldIndex, { type: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {fieldTypes.map(type => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Field Label *</Label>
                              <Input
                                value={field.label}
                                onChange={(e) => updateField(stepIndex, fieldIndex, { label: e.target.value })}
                                placeholder="e.g., Size"
                              />
                            </div>
                          </div>

                          {/* Field Name and Placeholder */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Field Name (Internal)</Label>
                              <Input
                                value={field.name}
                                onChange={(e) => updateField(stepIndex, fieldIndex, { name: e.target.value })}
                                placeholder="e.g., product_size"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Placeholder</Label>
                              <Input
                                value={field.placeholder}
                                onChange={(e) => updateField(stepIndex, fieldIndex, { placeholder: e.target.value })}
                                placeholder="Hint text..."
                              />
                            </div>
                          </div>

                          {/* Help Text */}
                          <div>
                            <Label className="text-xs">Help Text</Label>
                            <Input
                              value={field.help_text}
                              onChange={(e) => updateField(stepIndex, fieldIndex, { help_text: e.target.value })}
                              placeholder="Additional guidance for customers..."
                            />
                          </div>

                          {/* Options for Radio/Dropdown/Checkbox */}
                          {['radio', 'dropdown', 'checkbox', 'button-select', 'image-cards', 'gallery'].includes(field.type) && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs">Options</Label>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addOption(stepIndex, fieldIndex)}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Option
                                </Button>
                              </div>
                              {field.options?.map((option, optionIndex) => (
                                <div key={optionIndex} className="flex gap-2 items-start p-3 bg-gray-50 rounded border">
                                  <div className="flex-1 space-y-2">
                                    <Input
                                      value={option.label}
                                      onChange={(e) => updateOption(stepIndex, fieldIndex, optionIndex, { label: e.target.value })}
                                      placeholder="Option label"
                                    />
                                    
                                    {/* Image Upload for image-cards and gallery */}
                                    {['image-cards', 'gallery'].includes(field.type) && (
                                      <div className="space-y-2">
                                        <Label className="text-xs">Option Image</Label>
                                        <div className="flex gap-2 items-center">
                                          <Input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                              const file = e.target.files[0];
                                              if (file) handleImageUpload(stepIndex, fieldIndex, optionIndex, file);
                                            }}
                                            className="flex-1"
                                          />
                                          {option.image_url && (
                                            <img
                                              src={option.image_url}
                                              alt="Preview"
                                              className="h-12 w-12 object-cover rounded border"
                                            />
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {field.price_impact && (
                                      <div className="flex items-center gap-2">
                                        <Label className="text-xs">Price:</Label>
                                        <div className="flex items-center gap-1">
                                          <DollarSign className="h-4 w-4 text-gray-500" />
                                          <Input
                                            type="number"
                                            value={option.default_price}
                                            onChange={(e) => updateOption(stepIndex, fieldIndex, optionIndex, { 
                                              default_price: parseFloat(e.target.value) || 0 
                                            })}
                                            placeholder="0"
                                            className="w-24"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeOption(stepIndex, fieldIndex, optionIndex)}
                                  >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* File Upload Configuration */}
                          {field.type === 'file_upload' && (
                            <div className="space-y-3 p-3 bg-amber-50 rounded border border-amber-200">
                              <Label className="text-sm font-medium flex items-center gap-2">
                                <Upload className="h-4 w-4" />
                                File Upload Settings
                              </Label>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">Max File Size (MB)</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={field.max_size || 10}
                                    onChange={(e) => updateField(stepIndex, fieldIndex, { 
                                      max_size: parseInt(e.target.value) || 10 
                                    })}
                                    placeholder="10"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Allowed File Types</Label>
                                  <Select
                                    value={field.allowed_types_preset || 'images'}
                                    onValueChange={(value) => {
                                      const typePresets = {
                                        'images': { types: ['image/*'], label: 'Images only' },
                                        'documents': { types: ['.pdf', '.doc', '.docx'], label: 'Documents only' },
                                        'images_docs': { types: ['image/*', '.pdf', '.doc', '.docx'], label: 'Images & Documents' },
                                        'all': { types: ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip'], label: 'All common files' }
                                      };
                                      const preset = typePresets[value];
                                      updateField(stepIndex, fieldIndex, { 
                                        allowed_types_preset: value,
                                        allowed_types: preset.types,
                                        allowed_types_label: preset.label
                                      });
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select allowed types" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="images">Images only</SelectItem>
                                      <SelectItem value="documents">Documents only</SelectItem>
                                      <SelectItem value="images_docs">Images & Documents</SelectItem>
                                      <SelectItem value="all">All common files</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Help Text</Label>
                                <Input
                                  value={field.help_text || ''}
                                  onChange={(e) => updateField(stepIndex, fieldIndex, { help_text: e.target.value })}
                                  placeholder="e.g., Upload your logo for printing (PNG or JPG)"
                                />
                              </div>
                            </div>
                          )}

                          {/* Toggles and Price Configuration */}
                          <div className="flex flex-col gap-4 p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-6">
                              <label className="flex items-center gap-2">
                                <Switch
                                  checked={field.required}
                                  onCheckedChange={(checked) => updateField(stepIndex, fieldIndex, { required: checked })}
                                />
                                <span className="text-sm font-medium">Required</span>
                              </label>
                              
                              <label className="flex items-center gap-2">
                                <Switch
                                  checked={field.price_impact}
                                  onCheckedChange={(checked) => updateField(stepIndex, fieldIndex, { price_impact: checked })}
                                />
                                <span className="text-sm font-medium flex items-center gap-1">
                                  <DollarSign className="h-4 w-4" />
                                  Price Impact
                                </span>
                              </label>

                              {field.price_impact && (
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs">Default:</Label>
                                  <Select
                                    value={field.default_mechanism}
                                    onValueChange={(value) => updateField(stepIndex, fieldIndex, { 
                                      default_mechanism: value,
                                      // Reset multiplier type when switching mechanism
                                      default_multiplier_type: value === 'addon' ? (field.default_multiplier_type || 'fixed') : undefined
                                    })}
                                  >
                                    <SelectTrigger className="w-32">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="variant">Variant</SelectItem>
                                      <SelectItem value="addon">Add-On</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                            
                            {/* NEW: Temporal Dependency - Only shows for Add-Ons */}
                            {field.price_impact && field.default_mechanism === 'addon' && (
                              <div className="p-3 bg-white rounded border border-blue-200">
                                <div className="flex items-center gap-3">
                                  <Label className="text-xs font-semibold whitespace-nowrap">Default Multiplier Type:</Label>
                                  <Select
                                    value={field.default_multiplier_type || 'fixed'}
                                    onValueChange={(value) => updateField(stepIndex, fieldIndex, { default_multiplier_type: value })}
                                  >
                                    <SelectTrigger className="w-48">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="fixed">
                                        <div className="flex flex-col">
                                          <span className="font-medium">Fixed Surcharge</span>
                                        </div>
                                      </SelectItem>
                                      <SelectItem value="temporal">
                                        <div className="flex flex-col">
                                          <span className="font-medium">Temporal Surcharge</span>
                                        </div>
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                  {field.default_multiplier_type === 'temporal' 
                                    ? '⏱️ Price will be multiplied by subscription duration (e.g., ₹500/month × 3 months = ₹1,500)'
                                    : '📌 Price is a one-time surcharge regardless of duration (e.g., Photo Album = ₹2,000 always)'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeField(stepIndex, fieldIndex)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
                )}
              </CardContent>
            </Card>
          ))}

          <Button onClick={addStep} variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Step
          </Button>
          </div>

          {/* Right: Live Preview Panel */}
          <div className="w-[500px] flex-shrink-0">
            <Card className="sticky top-0 h-full overflow-hidden flex flex-col">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 border-b">
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-blue-600" />
                  Live Preview
                </CardTitle>
                <p className="text-xs text-gray-600 mt-1">
                  How customers will see this form
                </p>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-6">
                {templateData.steps.map((step, stepIndex) => (
                  <div key={step.id} className="space-y-4">
                    {/* Visual Guide Layout Preview */}
                    {step.layout === 'visual_guide' ? (
                      <div className="rounded-lg overflow-hidden border shadow-sm">
                        <div className="grid grid-cols-2">
                          {/* Image Panel */}
                          <div className={`p-4 flex flex-col items-center justify-center ${
                            step.bg_style === 'light' ? 'bg-gray-100' : 
                            step.bg_style === 'gradient' ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 
                            'bg-gray-900'
                          }`}>
                            {step.guide_image ? (
                              <img 
                                src={step.guide_image.startsWith('http') ? step.guide_image : `${process.env.REACT_APP_BACKEND_URL}${step.guide_image}`}
                                alt="Guide"
                                className="max-w-full max-h-32 object-contain rounded"
                              />
                            ) : (
                              <div className="w-full h-24 bg-gray-700 rounded flex items-center justify-center">
                                <ImageIcon className="h-8 w-8 text-gray-500" />
                              </div>
                            )}
                            {step.description && (
                              <p className="text-gray-300 text-xs mt-2 text-center">{step.description}</p>
                            )}
                          </div>
                          
                          {/* Fields Panel */}
                          <div className="bg-white p-4">
                            <h4 className="font-semibold text-sm mb-3">{step.name}</h4>
                            {step.fields.length === 0 ? (
                              <p className="text-xs text-gray-400">Add fields to preview</p>
                            ) : (
                              <div className="space-y-2">
                                {step.fields.map((field) => (
                                  <div key={field.id} className="flex items-center gap-2">
                                    <span className="text-xs font-medium w-16 truncate">{field.label}</span>
                                    <input 
                                      type="text" 
                                      className="flex-1 px-2 py-1 text-xs border rounded"
                                      placeholder={field.placeholder || ''}
                                      value={previewValues[field.name] || ''}
                                      onChange={(e) => setPreviewValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                                    />
                                    {field.unit && <span className="text-xs text-gray-500">{field.unit}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Navigation Preview */}
                        <div className="flex justify-between p-3 bg-gray-50 border-t">
                          <button className="px-3 py-1 text-xs border rounded">Back</button>
                          <button className="px-3 py-1 text-xs bg-yellow-500 text-black rounded font-medium">Next</button>
                        </div>
                      </div>
                    ) : (
                      /* Standard Layout Preview */
                      <>
                        {templateData.steps.length > 1 && (
                          <div className="pb-3 border-b">
                            <h3 className="font-semibold text-lg text-gray-900">{step.name}</h3>
                            {step.description && (
                              <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                            )}
                          </div>
                        )}
                        
                        {step.fields.length === 0 ? (
                          <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-lg">
                            <p className="text-sm">No fields added yet</p>
                            <p className="text-xs mt-1">Add fields to see preview</p>
                          </div>
                        ) : (
                          step.fields.map((field) => (
                            <ProductOptionFieldRenderer
                              key={field.id}
                              field={field}
                              value={previewValues[field.name]}
                              onChange={(name, value) => 
                                setPreviewValues(prev => ({ ...prev, [name]: value }))
                              }
                            />
                          ))
                        )}
                      </>
                    )}
                  </div>
                ))}
                
                {templateData.steps.every(step => step.fields.length === 0) && (
                  <div className="text-center py-12 text-gray-400">
                    <Eye className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm font-medium">Start adding fields</p>
                    <p className="text-xs mt-1">Your form preview will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Template List View
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Product Option Templates</h1>
            <p className="text-gray-600 mt-1">
              Create dynamic forms for product customization
            </p>
          </div>
          <div className="flex gap-2">
            {/* Import/Export Buttons */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportJSON}
              className="hidden"
              data-testid="import-file-input"
            />
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              data-testid="import-json-btn"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileUp className="h-4 w-4 mr-2" />
              )}
              Import JSON
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportJSON}
              data-testid="export-json-btn"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            <Button variant="outline" onClick={() => setShowAIDialog(true)}>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate with AI
            </Button>
            <Button onClick={handleCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              Create Manually
            </Button>
          </div>
        </div>

        {/* Pricing Config Banner */}
        <Card className={`border-2 ${pricingEnabled ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-semibold flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Master Pricing Change Switch
                </h3>
                <p className="text-sm mt-1">
                  {pricingEnabled 
                    ? 'Automated price modifications are enabled. Prices will sync automatically.'
                    : 'Price modifications disabled. Additional amounts handled separately via WhatsApp.'}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <Badge variant={pricingEnabled ? 'default' : 'secondary'} className="text-sm">
                  {pricingEnabled ? 'ENABLED' : 'DISABLED'}
                </Badge>
                <Switch
                  checked={pricingEnabled}
                  onCheckedChange={togglePricingConfig}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates List */}
        <div className="grid gap-4">
          {templates.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-500">No templates created yet</p>
                <Button onClick={handleCreateNew} className="mt-4">
                  Create Your First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            templates.map(template => (
              <Card key={template.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold">{template.name}</h3>
                        {!template.is_active && (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {template.tags.map(tag => (
                          <Badge key={tag} variant="outline">
                            <Tag className="h-3 w-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>{template.steps.length} steps</span>
                        <span>•</span>
                        <span>
                          {template.steps.reduce((acc, step) => acc + step.fields.length, 0)} fields
                        </span>
                        {template.category_ids?.length > 0 && (
                          <>
                            <span>•</span>
                            <div className="flex items-center gap-1 flex-wrap">
                              <FolderTree className="h-4 w-4" />
                              {/* Show first 2 category names */}
                              {template.category_ids.slice(0, 2).map((catId, idx) => {
                                const cat = categories.find(c => c.id === catId);
                                return cat ? (
                                  <Badge key={catId} variant="secondary" className="text-xs">
                                    {cat.name}
                                  </Badge>
                                ) : null;
                              })}
                              {/* Show +X if more than 2 categories */}
                              {template.category_ids.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{template.category_ids.length - 2} more
                                </Badge>
                              )}
                              {/* Show subcategory count if any */}
                              {template.subcategory_ids?.length > 0 && (
                                <Badge variant="outline" className="text-xs text-gray-500">
                                  +{template.subcategory_ids.length} subcategories
                                </Badge>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        title="Duplicate template"
                        data-testid={`duplicate-template-${template.id}`}
                        onClick={() => handleDuplicateTemplate(template)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* AI Generation Dialog */}
        {showAIDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                    Generate Template with AI
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowAIDialog(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Describe your product customization needs</Label>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Example: Create a t-shirt customization form with size, color, print design options, and a field for custom text"
                    rows={4}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Be specific about what options customers should be able to select
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">💡 Tips for better results:</h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Mention the product type (t-shirt, cake, service, etc.)</li>
                    <li>List specific customization options needed</li>
                    <li>Specify if you need image selection or text inputs</li>
                    <li>Include any special requirements or constraints</li>
                  </ul>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> After generation, you can customize field types, add pricing, 
                    and assign to categories.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowAIDialog(false)}
                    disabled={aiLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAIGenerate}
                    disabled={aiLoading || !aiPrompt.trim()}
                  >
                    {aiLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate Template
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProductOptionTemplateBuilder;
